/**
 * Serialize basket-mutating SCAPI calls on the cart/checkout so their responses
 * apply to the shared React Query basket cache in FIFO order.
 *
 * WHY (KL-1029): every basket mutation — `removeItemFromBasket`,
 * `updateShippingMethodForShipment`, `addCouponToBasket` /
 * `removeCouponFromBasket`, and the deposit `updateBasket` PATCH — writes the
 * SAME `getCustomerBaskets` React Query cache via the Commerce SDK's
 * `onSuccess` → `setQueryData`, with last-write-wins semantics and no
 * serialization between them. When a shopper removes a line item while another
 * basket call is in flight (change delivery method, apply promo code, or the
 * deposit auto-sync effect that fires on `productItems` changes), a response
 * built on the PRE-removal basket can land last and resurrect the removed item
 * (and its option/child line items).
 *
 * Serializing every basket mutation through a single promise chain guarantees a
 * mutation starts only after the previous one has fully settled (including its
 * synchronous cache write). SCAPI therefore always processes each request
 * against the latest basket, so no in-flight response can restore a line item
 * that a prior queued mutation already removed.
 *
 * This is a client-runtime queue: all callers are user-driven event handlers /
 * effects that never run during SSR, so the module-level chain is safe.
 */

let chain = Promise.resolve();

/**
 * Enqueue a basket mutation. The task runs only after all previously enqueued
 * tasks have settled. The returned promise resolves/rejects with the task's own
 * result so callers keep their existing try/catch and `.then/.catch` handling.
 *
 * @template T
 * @param {() => Promise<T>} task - Runs the actual mutation (e.g. `mutateAsync`).
 * @returns {Promise<T>} Resolves/rejects with the task result.
 */
export function enqueueBasketMutation(task) {
  const run = chain.then(() => task());

  // Advance the chain once this task settles (success OR failure) so a rejected
  // mutation never wedges the queue. The caller still sees the real outcome via
  // the returned `run` promise.
  chain = run.then(
    () => undefined,
    () => undefined
  );

  return run;
}
